import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon, FunnelIcon } from '@heroicons/react/24/outline';

/**
 * SearchAndFilters component - Provides search and filter functionality for the network directory
 * Features real-time search with debouncing, case-insensitive filtering, and location filtering
 */
const SearchAndFilters = ({ 
  searchTerm = '', 
  onSearchChange,
  filters = {},
  onFilterChange,
  users = [], // New prop to receive user data for dynamic location generation
  className = '' 
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [selectedFilters, setSelectedFilters] = useState({
    locations: filters.locations || []
  });
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  // Generate location options dynamically from users data
  const locationOptions = useMemo(() => {
    const locationSet = new Set();
    
    users.forEach(user => {
      // Skip hidden users explicitly (extra safety check)
      if (user.isHidden === true) {
        return;
      }
      
      const location = user.basicInfo?.location;
      if (location && location.trim()) {
        // Clean and normalize location
        const cleanLocation = location.trim();
        
        // Handle remote/anywhere variations
        if (cleanLocation.toLowerCase().includes('remote') || 
            cleanLocation.toLowerCase().includes('anywhere')) {
          locationSet.add('Remote');
        } else {
          locationSet.add(cleanLocation);
        }
      }
    });

    // Convert to array and sort alphabetically, with Remote at the end
    const locations = Array.from(locationSet).sort((a, b) => {
      if (a === 'Remote') return 1;
      if (b === 'Remote') return -1;
      return a.localeCompare(b);
    });

    // Convert to option format - use the exact location string as both value and label
    return locations.map(location => ({
      value: location, // Use exact location string as value
      label: location
    }));
  }, [users]);

  // Filter categories - now only contains locations
  const filterCategories = [
    {
      key: 'locations',
      label: 'Location',
      options: locationOptions
    }
  ];

  // Debounce search input to improve performance
  const debounceDelay = 300; // 300ms delay

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((term) => {
      if (onSearchChange) {
        onSearchChange(term);
      }
    }, debounceDelay),
    [onSearchChange]
  );

  // Update local search term and trigger debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    debouncedSearch(value);
  };

  // Clear search functionality
  const handleClearSearch = () => {
    setLocalSearchTerm('');
    if (onSearchChange) {
      onSearchChange('');
    }
  };

  // Handle filter changes
  const handleFilterToggle = (category, value) => {
    const currentCategoryFilters = selectedFilters[category];
    const newCategoryFilters = currentCategoryFilters.includes(value)
      ? currentCategoryFilters.filter(item => item !== value)
      : [...currentCategoryFilters, value];
    
    const newSelectedFilters = {
      ...selectedFilters,
      [category]: newCategoryFilters
    };
    
    setSelectedFilters(newSelectedFilters);
    
    if (onFilterChange) {
      onFilterChange(newSelectedFilters);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    const clearedFilters = {
      locations: []
    };
    setSelectedFilters(clearedFilters);
    if (onFilterChange) {
      onFilterChange(clearedFilters);
    }
  };

  // Toggle filter dropdown
  const toggleFilterDropdown = () => {
    setIsFilterDropdownOpen(!isFilterDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isFilterDropdownOpen && !event.target.closest('.filter-dropdown')) {
        setIsFilterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterDropdownOpen]);

  // Sync with external props changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    setSelectedFilters({
      locations: filters.locations || []
    });
  }, [filters]);

  return (
    <div className={`w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
        {/* Search Input */}
        <div className="relative flex-1 w-full">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 transition-colors duration-200" aria-hidden="true" />
          </div>
          
          <input
            type="text"
            value={localSearchTerm}
            onChange={handleSearchChange}
            placeholder="Search by name, company, or role..."
            className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg 
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 
                     placeholder-gray-500 dark:placeholder-gray-400
                     focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                     hover:border-gray-400 dark:hover:border-gray-500
                     shadow-sm hover:shadow-md focus:shadow-md
                     transition-all duration-200 ease-out"
            aria-label="Search network directory"
          />
          
          {/* Clear Search Button */}
          {localSearchTerm && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                type="button"
                onClick={handleClearSearch}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 
                         hover:bg-gray-100 dark:hover:bg-gray-700
                         transition-all duration-200 ease-out focus:outline-none focus:ring-2 
                         focus:ring-purple-500 rounded-full p-1.5"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Filter Dropdown */}
        <div className="relative filter-dropdown w-full sm:w-auto">
          <button
            type="button"
            onClick={toggleFilterDropdown}
            className={`inline-flex items-center justify-center w-full sm:w-auto px-4 py-3 border border-gray-300 dark:border-gray-600 
                       rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 
                       hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500
                       focus:outline-none focus:ring-2 focus:ring-purple-500 
                       shadow-sm hover:shadow-md focus:shadow-md
                       transition-all duration-200 ease-out ${
                         selectedFilters.locations.length > 0 ? 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-600' : ''
                       }`}
            aria-label="Filter by location"
            aria-expanded={isFilterDropdownOpen}
          >
            <FunnelIcon className="h-5 w-5 mr-2 transition-colors duration-200" aria-hidden="true" />
            <span className="text-sm font-medium whitespace-nowrap">
              {selectedFilters.locations.length > 0 
                ? `Filters (${selectedFilters.locations.length})` 
                : 'Filter'
              }
            </span>
            <ChevronDownIcon 
              className={`h-4 w-4 ml-2 transition-transform duration-200 ease-out ${
                isFilterDropdownOpen ? 'rotate-180' : ''
              }`} 
              aria-hidden="true" 
            />
          </button>

          {/* Dropdown Menu */}
          {isFilterDropdownOpen && (
            <div className="absolute right-0 sm:right-0 left-0 sm:left-auto mt-2 w-full sm:w-80 
                           bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                           rounded-lg shadow-xl dark:shadow-2xl z-20
                           backdrop-blur-sm bg-white/95 dark:bg-gray-800/95 filter-dropdown-scroll">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Filter Members
                  </h3>
                  {selectedFilters.locations.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 
                               dark:hover:text-purple-300 font-medium
                               hover:bg-purple-50 dark:hover:bg-purple-900/30 px-2 py-1 rounded
                               transition-all duration-200 ease-out"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {/* Filter Categories */}
                <div className="space-y-4">
                  {filterCategories.map((category) => (
                    <div key={category.key}>
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                        {category.label}
                      </h4>
                      <div className="space-y-1 scrollbar-styled max-h-32">
                        {category.options.map((option) => (
                          <label
                            key={option.value}
                            className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 
                                     rounded-md p-2 transition-all duration-200 ease-out group"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFilters[category.key].includes(option.value)}
                              onChange={() => handleFilterToggle(category.key, option.value)}
                              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 
                                       dark:border-gray-600 rounded transition-colors duration-200"
                            />
                            <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors duration-200">
                              {option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {(localSearchTerm || selectedFilters.locations.length > 0) && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {localSearchTerm && (
            <div className="text-sm text-gray-500 dark:text-gray-400 
                          bg-gray-50 dark:bg-gray-800 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700">
              Searching for "<span className="font-medium text-gray-700 dark:text-gray-300">{localSearchTerm}</span>"
            </div>
          )}
          
          {/* Display active filters */}
          {Object.entries(selectedFilters).map(([categoryKey, selectedValues]) => 
            selectedValues.length > 0 && (
              <div key={categoryKey} className="flex flex-wrap gap-2">
                {selectedValues.map((value) => {
                  const category = filterCategories.find(cat => cat.key === categoryKey);
                  const option = category?.options.find(opt => opt.value === value);
                  return (
                    <span
                      key={`${categoryKey}-${value}`}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium 
                               bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300
                               border border-purple-200 dark:border-purple-700
                               hover:bg-purple-200 dark:hover:bg-purple-900/50
                               transition-all duration-200 ease-out"
                    >
                      <span className="text-xs text-purple-600 dark:text-purple-400 mr-1.5 font-semibold">
                        {category?.label}:
                      </span>
                      {option?.label}
                      <button
                        type="button"
                        onClick={() => handleFilterToggle(categoryKey, value)}
                        className="ml-1.5 hover:text-purple-600 dark:hover:text-purple-400
                                 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5
                                 transition-all duration-200 ease-out"
                        aria-label={`Remove ${option?.label} ${category?.label} filter`}
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Debounce utility function
 * Delays function execution until after specified delay has passed since last invocation
 */
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

export default SearchAndFilters;